import React from 'react';
import { motion } from 'framer-motion';
import { useTheme, useAnimationHelpers, Grid, Card, Section } from '../design-system';

// Design System Constants - Fixed Spacing
const DESIGN_SYSTEM = {
  spacing: {
    xs: 'space-y-2',
    sm: 'space-y-3', 
    md: 'space-y-4',
    lg: 'space-y-6',
    xl: 'space-y-8'
  },
  container: {
    sm: 'max-w-4xl',
    md: 'max-w-5xl', 
    lg: 'max-w-6xl',
    xl: 'max-w-7xl'
  },
  padding: {
    sm: 'p-4 md:p-6',
    md: 'p-6 md:p-8',
    lg: 'p-8 md:p-10',
    xl: 'p-10 md:p-12'
  },
  text: {
    badge: 'text-sm font-semibold tracking-wide',
    heading1: 'text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold tracking-tight',
    heading2: 'text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight',
    heading3: 'text-2xl md:text-3xl font-bold tracking-tight',
    body: 'text-lg md:text-xl leading-relaxed',
    caption: 'text-base md:text-lg leading-relaxed'
  },
  cards: {
    sm: 'rounded-2xl bg-gradient-to-br from-dark-bg-lighter/80 to-dark-bg/80 backdrop-blur-md border border-white/10',
    md: 'rounded-3xl bg-gradient-to-br from-dark-bg-lighter/80 to-dark-bg/80 backdrop-blur-md border border-white/10',
    lg: 'rounded-3xl bg-gradient-to-br from-dark-bg-lighter/90 to-dark-bg/90 backdrop-blur-xl border border-white/15'
  },
  buttons: {
    primary: 'bg-gradient-to-r from-ripple-blue to-ripple-blue-light hover:from-ripple-blue/90 hover:to-ripple-blue-light/90 text-white font-semibold rounded-xl transition-all duration-300 shadow-lg shadow-ripple-blue/25 border border-ripple-blue/30',
    secondary: 'bg-white/5 hover:bg-white/10 backdrop-blur-md border border-white/20 hover:border-white/30 text-white font-semibold rounded-xl transition-all duration-300'
  }
};

// Professional Web3 Icons
const DollarIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1.41 16.09V20h-2.67v-1.93c-1.71-.36-3.16-1.46-3.27-3.4h1.96c.1 1.05.82 1.87 2.65 1.87 1.96 0 2.4-.98 2.4-1.59 0-.83-.44-1.61-2.67-2.14-2.48-.6-4.18-1.62-4.18-3.67 0-1.72 1.39-2.84 3.11-3.21V4h2.67v1.95c1.86.45 2.79 1.86 2.85 3.39H14.3c-.05-1.11-.64-1.87-2.22-1.87-1.5 0-2.4.68-2.4 1.64 0 .84.65 1.39 2.67 1.91 2.28.6 4.18 1.58 4.18 3.91 0 1.82-1.33 2.96-3.12 3.16z"/>
  </svg>
);

const TrendingIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path d="M16 6l2.29 2.29-4.88 4.88-4-4L2 16.59 3.41 18l6-6 4 4 6.3-6.29L22 12V6z"/>
  </svg>
);

const SpeedIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path d="M20.38 8.57l-1.23 1.85a8 8 0 0 1-.22 7.58H5.07A8 8 0 0 1 15.58 6.85l1.85-1.23A10 10 0 0 0 3.35 19a2 2 0 0 0 1.72 1h13.85a2 2 0 0 0 1.74-1 10 10 0 0 0-.27-10.44l-.01.01zM12 10a2 2 0 1 0 0 4 2 2 0 0 0 0-4z"/>
  </svg>
);

const NetworkIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-7h2v7zm4 0h-2V7h2v10zm4 0h-2v-4h2v4z"/>
  </svg>
);

const ShieldIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path d="M12,1L3,5V11C3,16.55 6.84,21.74 12,23C17.16,21.74 21,16.55 21,11V5L12,1M12,7C13.4,7 14.8,8.6 14.8,10V11.5C15.4,11.5 16,12.1 16,12.7V16.8C16,17.4 15.4,18 14.8,18H9.2C8.6,18 8,17.4 8,16.8V12.7C8,12.1 8.6,11.5 9.2,11.5V10C9.2,8.6 10.6,7 12,7M12,8.2C11.2,8.2 10.5,8.7 10.5,10V11.5H13.5V10C13.5,8.7 12.8,8.2 12,8.2Z"/>
  </svg>
);

const SupportIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path d="M19 2H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h4l3 3 3-3h4c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-6 16h-2v-2h2v2zm2.07-7.75l-.9.92C13.45 11.9 13 12.5 13 14h-2v-.5c0-1.1.45-2.1 1.17-2.83l1.24-1.26c.37-.36.59-.86.59-1.41 0-1.1-.9-2-2-2s-2 .9-2 2H8c0-2.21 1.79-4 4-4s4 1.79 4 4c0 .88-.36 1.68-.93 2.25z"/>
  </svg>
);

const ArrowIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 20 20">
    <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
  </svg>
);

const Stats: React.FC = () => {
  const { tokens, components, utils } = useTheme();
  const { fadeInUp, stagger } = useAnimationHelpers();

  const stats = [
    {
      number: "$127.8M",
      label: "Protocol Liquidity Secured",
      sublabel: "",
      icon: <DollarIcon className="w-6 h-6" />,
      color: "ripple-blue"
    },
    {
      number: "6.8%",
      label: "Net Yield (Stable & Transparent)",
      sublabel: "",
      icon: <TrendingIcon className="w-6 h-6" />,
      color: "accent-blue"
    },
    {
      number: "3–5s",
      label: "Transaction Finality on XRPL",
      sublabel: "",
      icon: <SpeedIcon className="w-6 h-6" />,
      color: "ripple-teal"
    },
    {
      number: "50+",
      label: "Integrated Protocols & Bridges",
      sublabel: "",
      icon: <NetworkIcon className="w-6 h-6" />,
      color: "accent-blue"
    },
    {
      number: "99.9%",
      label: "Reliable Platform Uptime",
      sublabel: "",
      icon: <ShieldIcon className="w-6 h-6" />,
      color: "ripple-teal"
    },
    {
      number: "24/7",
      label: "Customer Support Access",
      sublabel: "",
      icon: <SupportIcon className="w-6 h-6" />,
      color: "ripple-blue"
    }
  ];

  return (
    <section className="relative py-8 md:py-12 lg:py-16 overflow-hidden">
      {/* Enhanced Light High-Tech Background */}
      <div className="absolute inset-0">
        {/* Light base gradient with depth */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-100 via-gray-50 to-slate-200"></div>
        
        {/* Layered light glass textures */}
        <div className="absolute inset-0">
          {/* Primary light glass layer */}
          <div className="absolute inset-0 bg-gradient-to-br from-white/40 via-transparent to-white/20 backdrop-blur-[1px]"></div>
          {/* Secondary light glass layer with different angle */}
          <div className="absolute inset-0 bg-gradient-to-tl from-slate-100/30 via-transparent to-blue-50/20 backdrop-blur-[0.5px]"></div>
        </div>
        
        {/* Enhanced geometric elements with glass effect */}
        <div className="absolute inset-0">
          <div className="absolute w-96 h-96 rounded-full bg-gradient-to-br from-ripple-blue/8 via-ripple-blue/4 to-transparent -top-48 -right-48 blur-3xl backdrop-blur-sm"></div>
          <div className="absolute w-80 h-80 rounded-full bg-gradient-to-br from-ripple-teal/6 via-ripple-teal/3 to-transparent -bottom-40 -left-40 blur-2xl backdrop-blur-sm"></div>
          {/* Additional floating elements */}
          <div className="absolute w-40 h-40 rounded-full bg-gradient-to-br from-blue-200/20 to-transparent top-1/4 right-1/3 blur-xl animate-pulse"></div>
          <div className="absolute w-32 h-32 rounded-full bg-gradient-to-br from-teal-200/20 to-transparent bottom-1/3 left-1/4 blur-lg animate-pulse" style={{animationDelay: '1.5s'}}></div>
        </div>
        
        {/* High-tech grid pattern */}
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: `
            radial-gradient(circle at 1px 1px, rgba(59,130,246,0.3) 1px, transparent 0),
            linear-gradient(90deg, rgba(59,130,246,0.1) 1px, transparent 1px),
            linear-gradient(rgba(59,130,246,0.1) 1px, transparent 1px)
          `,
          backgroundSize: '40px 40px, 40px 40px, 40px 40px'
        }}></div>
        
        {/* Noise texture for premium feel */}
        <div className="absolute inset-0 opacity-[0.015]" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)' opacity='0.4'/%3E%3C/svg%3E")`
        }}></div>

        {/* Subtle animated shimmer effect */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -skew-x-12 animate-shimmer"></div>
        </div>
      </div>

      {/* Main Content Container */}
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 xl:px-16 relative z-10">
        
        {/* Light glass container for header */}
        <div className="relative backdrop-blur-sm bg-white/20 rounded-3xl border border-white/30 shadow-2xl shadow-black/5 p-8 md:p-12 mb-12">
          {/* Inner glow effect */}
          <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-white/10 via-transparent to-white/5 pointer-events-none"></div>
          
          {/* Trusted by thousands heading */}
          <motion.div 
            className="text-center max-w-4xl mx-auto relative z-10"
            {...fadeInUp}
          >
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight leading-[0.95] mb-6 text-gray-900 drop-shadow-sm">
              Trusted by{' '}
              <span className="bg-gradient-to-r from-ripple-blue via-ripple-blue-light to-ripple-teal bg-clip-text text-transparent drop-shadow-sm">
                thousands
              </span>
            </h2>
            
            <p className="text-lg leading-relaxed text-gray-700 max-w-2xl mx-auto drop-shadow-sm">
              Join thousands of users already earning yield, trading digital assets, and building wealth through real utility on the XRP Ledger.
            </p>
          </motion.div>
        </div>

        {/* Enhanced Stats Grid with premium light glass cards */}
        <motion.div
          variants={stagger.container}
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
          className="mb-16"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                variants={stagger.item}
                className="relative group"
                whileHover={{ y: -6 }}
              >
                {/* Light glass card with enhanced effects */}
                <div className="bg-white/40 backdrop-blur-xl border border-white/30 rounded-2xl p-6 text-center transition-all duration-300 shadow-xl shadow-black/5 group-hover:bg-white/60 group-hover:border-white/50 group-hover:shadow-2xl group-hover:shadow-black/10">
                  
                  {/* Inner highlight */}
                  <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-white/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
                  
                  {/* Content */}
                  <div className="relative z-10">
                    {/* Enhanced Icon with light glass background */}
                    <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-gradient-to-br from-ripple-blue/20 via-ripple-blue/10 to-transparent backdrop-blur-md border border-ripple-blue/20 shadow-lg shadow-ripple-blue/10 group-hover:shadow-ripple-blue/20 transition-all duration-300">
                      <div className="text-ripple-blue">
                        {stat.icon}
                      </div>
                    </div>

                    {/* Stat Number with enhanced styling */}
                    <div className="text-3xl font-semibold text-gray-900 mb-2 drop-shadow-sm">
                      {stat.number}
                    </div>

                    {/* Stat Label */}
                    <div className="text-sm font-medium text-gray-700 leading-tight">
                      {stat.label}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Enhanced CTA Section with light glass morphism */}
        <motion.div
          className="text-center"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          viewport={{ once: true }}
        >
          {/* Light glass container for CTA */}
          <div className="relative backdrop-blur-sm bg-white/20 rounded-3xl border border-white/30 shadow-2xl shadow-black/5 p-8 max-w-2xl mx-auto">
            {/* Inner glow effect */}
            <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-white/10 via-transparent to-white/5 pointer-events-none"></div>
            
            <div className="relative z-10">
              <motion.button
                className="px-10 py-4 bg-gradient-to-r from-ripple-blue to-ripple-blue-light text-white font-semibold rounded-2xl hover:shadow-xl hover:shadow-ripple-blue/30 transition-all duration-300 inline-flex items-center space-x-2 backdrop-blur-sm"
                whileHover={{ scale: 1.02, y: -1 }}
                whileTap={{ scale: 0.98 }}
              >
                <span>Join Community</span>
                <ArrowIcon className="w-4 h-4" />
              </motion.button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Stats; 